/**
 *
 * Styles for IconButton
 *
 */

import styled, { css } from "styled-components";
import { key } from "styled-theme";
import { sizes, breakpoints } from "root/libs/ui-styleguide/src";
import Icon from "../../Icons/Icon";

const hoverButton = css`
  &:hover {
    outline: 1px solid transparent;
    border: var(--dui-size-width-border-xs) solid transparent;
  }
`;

const primaryButton = css`
  background-color: var(--dui-color-red-500);
  transition: ease-in-out 200ms background-color;

  &:focus {
    box-shadow: 0 0 0 4px white, 0 0 0 6px var(--dui-color-red-500);
  }

  /* Leave this after the focus style to prevent the box-shadow to appear on hover of a focused button */
  &:hover {
    background-color: var(--dui-color-red-400);
    box-shadow: unset;
  }

  &:disabled {
    color: var(--dui-color-white-500);
    border: var(--dui-size-width-border-xs) solid transparent;
    background-color: var(--dui-color-gray-300);
  }
`;

const secondaryButton = css`
  background-color: var(--dui-color-white-500);
  color: var(--dui-color-red-500);
  border: var(--dui-size-width-border-xs) solid var(--dui-color-red-500);

  &:focus,
  &:active {
    box-shadow: 0 0 0 4px white, 0 0 0 6px var(--dui-color-red-500);
    border-width: 2px;
    padding: calc(${sizes.inputVerticalPaddingsBig} - 1px) calc(5px - 1px);
  }

  /* Leave this after the focus style to prevent the box-shadow to appear on hover of a focused button */
  &:hover {
    border-color: var(--dui-color-red-400);
    color: var(--dui-color-red-400);
    box-shadow: unset;
  }

  &:disabled {
    background-color: var(--dui-color-white-500);
    color: var(--dui-color-gray-400);
    border-color: var(--dui-color-gray-300);
  }
`;

const transparentButton = css`
  ${secondaryButton}
  background-color: transparent;

  &:focus,
  &:active {
    box-shadow: 0 0 0 4px var(--dui-color-yellow-500),
      0 0 0 6px var(--dui-color-red-500);
    border-width: 2px;
  }
`;

const tertiaryButton = css`
  transition: box-shadow 0.3s ease-out;
  color: var(--dui-color-black-500);
  background-color: var(--dui-color-white-500);
  border-color: var(--dui-color-white-500);
  font-weight: 800;
  box-shadow: 0 0 6px rgba(0, 0, 0, 0.3);

  &:focus,
  &:active {
    transition: none;
    border: var(--dui-size-width-border-sm) solid var(--dui-color-black-500);
    padding: calc(${sizes.inputVerticalPaddingsBig} - 1px) calc(5px - 1px);
  }

  /* Leave this after the focus style to prevent the box-shadow to appear on hover of a focused button */
  &:hover {
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.4);
  }

  &:disabled {
    color: var(--dui-color-gray-400);

    svg {
      fill: var(--dui-color-gray-400);
    }
  }
`;
const outlineBlackButton = css`
  border-color: var(--dui-color-black-500);
  background-color: transparent;
  color: var(--dui-color-black-500);
  &:hover {
    border: var(--dui-size-width-border-xs) solid var(--dui-color-black-500);
    opacity: 0.75;
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.4);
  }
`;

export const StyledIconButton = styled(Icon)`
  border-radius: ${(props) => (props.variation === "circle" ? "50%" : "4px")};
  width: ${(props) => props.buttonWidth.mobile};
  height: ${(props) => props.buttonHeight.mobile};
  text-align: center;
  line-height: ${key("fonts.desktop.button.lineHeight")};
  padding: 0;
  border: var(--dui-size-width-border-xs) solid transparent;
  display: flex;
  align-items: center;
  justify-content: center;

  @media screen and (min-width: ${breakpoints.tablet}) {
    width: ${(props) => props.buttonWidth.desktop};
    height: ${(props) => props.buttonHeight.desktop};
  }

  ${(props) => (props.buttonStyle === "primary" ? primaryButton : "")}
  ${(props) => (props.buttonStyle === "secondary" ? secondaryButton : "")}
  ${(props) => (props.buttonStyle === "transparent" ? transparentButton : "")}
  ${(props) => (props.buttonStyle === "tertiary" ? tertiaryButton : "")}
  ${hoverButton}
  ${(props) => (props.buttonStyle === "outlineBlack" ? outlineBlackButton : "")}

`;
